import { memo, useMemo } from 'react';
import { DEPENDENCY_PATH_COLORS } from '@/constants/timeline';

export interface DependencyConnectorProps {
  /** Start point (P50 finish of predecessor) */
  from: { x: number; y: number };
  /** End point (P50 start of dependent) */
  to: { x: number; y: number };
  /** Whether this is an incoming (predecessor) or outgoing (dependent) connection */
  type: 'incoming' | 'outgoing';
  /** Whether this connection is highlighted (hovered) */
  isHighlighted?: boolean;
  /** Whether the target is in a collapsed group */
  isCollapsed?: boolean;
  /** Direction relative to selected item (for enhanced color coding) */
  direction?: 'blocking' | 'following';
  /** Opacity override based on depth from selected item (0-1) */
  opacity?: number;
}

/**
 * DependencyConnector atom - SVG bezier curve connecting two deliverables
 *
 * Draws a smooth S-curve (horizontal bezier) connecting the P50 finish of
 * a predecessor to the P50 start of a dependent deliverable.
 *
 * Visual design:
 * - Incoming (predecessors): Blue tones - these block the current item
 * - Outgoing (dependents): Orange tones - current item blocks these
 * - Collapsed targets: Dashed line indicating target is hidden
 * - 12px invisible stroke for easier hover targeting
 */
export const DependencyConnector = memo(function DependencyConnector({
  from,
  to,
  type,
  isHighlighted = false,
  isCollapsed = false,
  direction,
  opacity,
}: DependencyConnectorProps) {
  // Calculate bezier control points for a smooth S-curve
  const pathData = useMemo(() => {
    // Use horizontal bezier with control points at midpoint
    const midX = (from.x + to.x) / 2;
    return `M ${from.x} ${from.y} C ${midX} ${from.y}, ${midX} ${to.y}, ${to.x} ${to.y}`;
  }, [from.x, from.y, to.x, to.y]);

  // Color scheme based on direction (enhanced) or type (legacy fallback)
  const colors = useMemo(() => {
    // Use direction-based colors from constants when available
    if (direction) {
      const directionColors = DEPENDENCY_PATH_COLORS[direction];
      return {
        stroke: directionColors.line,
        strokeHighlight: directionColors.line,
        fill: directionColors.background,
      };
    }

    // Legacy fallback based on type
    if (type === 'incoming') {
      // Blue tones for predecessors (these block us)
      return {
        stroke: isHighlighted ? '#3b82f6' : '#93c5fd', // blue-500 / blue-300
        strokeHighlight: '#3b82f6', // blue-500
        fill: '#dbeafe', // blue-100
      };
    } else {
      // Orange/amber tones for dependents (we block these)
      return {
        stroke: isHighlighted ? '#f59e0b' : '#fcd34d', // amber-500 / amber-300
        strokeHighlight: '#f59e0b', // amber-500
        fill: '#fef3c7', // amber-100
      };
    }
  }, [type, isHighlighted, direction]);

  // Stroke properties based on state
  const strokeWidth = isHighlighted ? 2.5 : 1.5;
  const strokeDasharray = isCollapsed ? '4,4' : undefined;
  // Use provided opacity (depth-based) or default
  const strokeOpacity = opacity ?? (isHighlighted ? 1 : 0.7);

  return (
    <g className="dependency-connector">
      {/* Invisible wider stroke for hover hit area */}
      <path d={pathData} fill="none" stroke="transparent" strokeWidth={12} className="cursor-pointer" />
      {/* Visible connection line */}
      <path
        d={pathData}
        fill="none"
        stroke={colors.stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        strokeOpacity={strokeOpacity}
        strokeLinecap="round"
        className="transition-all duration-200"
      />
      {/* Arrow marker at the end (pointing to dependent) */}
      <circle
        cx={to.x}
        cy={to.y}
        r={isHighlighted ? 4 : 3}
        fill={colors.stroke}
        fillOpacity={strokeOpacity}
        className="transition-all duration-200"
      />
    </g>
  );
});
